import React, { useEffect, useRef, useState } from "react";
import { Plus, ChevronDown, ArrowUp, Loader2, X, Layers, Wand2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";

export const SCOPES = ["Tout le portefeuille", "Athlètes", "Contrats", "Carrière", "Opportunités", "Relations", "Documents"].map((s) => tfr(s));

export const ACTIONS = [
  { id: "analyser", label: "Analyser", build: (m) => m },
  { id: "prioriser", label: tfr("Prioriser"), build: (m) => (m ? `${tfr("Priorise ces éléments :")} ${m}` : tfr("Que dois-je prioriser cette semaine ?")) },
  { id: "resumer", label: tfr("Résumer"), build: (m) => (m ? `${tfr("Résume :")} ${m}` : tfr("Fais un brief synthétique de mon portefeuille : actions urgentes, contrats à renouveler, opportunités et relations à surveiller")) },
  { id: "rdv", label: tfr("Préparer un rendez-vous"), build: (m) => (m ? `${tfr("Prépare un rendez-vous :")} ${m}` : tfr("Prépare mon prochain rendez-vous")) },
  { id: "plan", label: tfr("Générer un plan d'action"), build: (m) => `${tfr("Génère un plan d'action opérationnel")}${m ? ` : ${m}` : ""}` },
];

function ContextPicker({ onPick }) {
  const { tf } = useLang();
  const [open, setOpen] = useState(false);
  const [players, setPlayers] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (open && players === null) {
      api.get("/players").then((r) => setPlayers(Array.isArray(r.data) ? r.data : [])).catch(() => setPlayers([]));
    }
  }, [open, players]);

  const list = (players || []).filter((p) => p.name?.toLowerCase().includes(filter.toLowerCase())).slice(0, 8);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={tf("Ajouter un athlète au contexte")}
          data-testid="intel-composer-add-context"
          className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <Plus className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" data-testid="intel-context-picker">
        <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold px-1 pb-1.5">{tf("Ajouter un athlète au contexte")}</p>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={tf("Rechercher un athlète…")}
          data-testid="intel-context-search"
          className="w-full h-8 rounded-md border border-border bg-transparent px-2.5 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <div className="mt-1.5 max-h-52 overflow-y-auto">
          {players === null && <p className="text-xs text-muted-foreground px-1 py-2">{tf("Chargement…")}</p>}
          {players !== null && list.length === 0 && <p className="text-xs text-muted-foreground px-1 py-2">{tf("Aucun athlète trouvé.")}</p>}
          {list.map((p) => (
            <button
              key={p.id}
              onClick={() => { onPick({ id: p.id, name: p.name }); setOpen(false); setFilter(""); }}
              data-testid={`intel-context-pick-${p.id}`}
              className="w-full text-left rounded-md px-2 py-1.5 text-xs hover:bg-secondary/60 transition-colors"
            >
              <span className="font-medium">{p.name}</span>
              {p.club && <span className="text-muted-foreground"> · {p.club}</span>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ToolbarSelect({ icon: Icon, value, rawOptions, tf, onChange, testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="h-8 inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring max-w-[180px]"
        >
          <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{value}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[200px]">
        {rawOptions.map((o) => (
          <DropdownMenuItem key={o} onClick={() => onChange(o)} data-testid={`${testid}-${o}`}
            className={cn("text-xs", tf(o) === value && "font-semibold")}>
            {tf(o)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Premium intelligence composer — multiline, scope + action selectors, athlete context.
 * Enter submits, Shift+Enter inserts a newline, IME composition respected.
 */
export const IntelligenceComposer = ({
  value, onChange, onSubmit, asking, scope, onScope, action, onAction,
  contextPlayer, onPickContext, onClearContext,
}) => {
  const { tf } = useLang();
  const taRef = useRef(null);
  const actionDef = ACTIONS.find((a) => a.label === action) || ACTIONS[0];
  const canSubmit = !asking && (value.trim().length > 0 || actionDef.id !== "analyser");

  const autoGrow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  };
  useEffect(autoGrow, [value]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (canSubmit) onSubmit();
    }
  };

  return (
    <div
      data-testid="intel-composer"
      className="rounded-2xl border border-border bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_rgba(15,23,42,0.05)] dark:shadow-none transition-colors duration-150 focus-within:border-foreground/35"
    >
      {contextPlayer && (
        <div className="flex items-center gap-2 px-4 pt-3" data-testid="intel-context-chip">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-athlynx/40 bg-athlynx/10 text-athlynx text-xs font-medium px-3 py-1">
            <Sparkles className="h-3 w-3" aria-hidden="true" /> {tf("Contexte :")} {contextPlayer.name}
            <button onClick={onClearContext} aria-label={tf("Fermer")} data-testid="intel-context-clear"
              className="ml-0.5 hover:text-foreground transition-colors">
              <X className="h-3 w-3" />
            </button>
          </span>
          <span className="text-[11px] text-muted-foreground hidden sm:inline">{tf("Les réponses sont centrées sur cet athlète.")}</span>
        </div>
      )}
      <textarea
        ref={taRef}
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={asking}
        placeholder={tf("Posez une question sur votre portefeuille...")}
        aria-label="Athlynx Intelligence"
        data-testid="intel-query-input"
        className="w-full resize-none bg-transparent px-4 pt-3.5 pb-1 text-sm leading-relaxed placeholder:text-muted-foreground/70 focus:outline-none min-h-[56px] disabled:opacity-60"
      />
      <div className="flex items-center gap-1.5 px-3 pb-3 pt-1">
        <ContextPicker onPick={onPickContext} />
        <ToolbarSelect icon={Layers} value={tf(scope)} rawOptions={SCOPES} tf={tf} onChange={onScope} testid="intel-scope-select" />
        <ToolbarSelect icon={Wand2} value={tf(action)} rawOptions={ACTIONS.map((a) => a.label)} tf={tf} onChange={onAction} testid="intel-action-select" />
        <div className="flex-1" />
        <Button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          aria-label={tf("Envoyer")}
          data-testid="intel-query-submit"
          className="h-9 w-9 p-0 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
        >
          {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};
